import { createContext, useContext, useEffect, useState, useCallback } from "react";

const DICT = {
  en: {
    // Common
    app_name: "UpchaarQ Doctor App",
    find_doctor: "Find a Doctor",
    book_now: "Book Now",
    back: "Back",
    loading: "Loading...",
    cancel: "Cancel",
    confirm: "Confirm",
    // Patient app
    patient_app_title: "Find trusted doctors near you",
    patient_app_subtitle: "Search by name, city or specialization. Book instantly. Track your queue live.",
    search_placeholder: "Doctor name, specialization or hospital",
    city_placeholder: "City (optional)",
    specialization_label: "Specialization",
    all_specializations: "All specializations",
    use_my_location: "Use my location",
    no_results: "No doctors found. Try a different search.",
    consultation_fee: "Consultation fee",
    room_cabin: "Room",
    avg_diag_time: "Avg diagnosis time",
    qualification: "Qualification",
    working_hours: "Working hours",
    current_token: "Current Token",
    waiting: "Waiting",
    completed: "Completed",
    your_position: "Your position",
    estimated_wait: "Estimated wait",
    minutes: "min",
    live: "LIVE",
    on_leave: "Doctor is currently on leave",
    book_appointment: "Book Appointment",
    your_name: "Your name",
    your_age: "Age",
    your_mobile: "Mobile number",
    pay_and_book: "Pay & Book via UPI",
    booking_confirmed: "Booking confirmed",
    token_number: "Token Number",
    pay_via_upi: "Pay via UPI",
    upi_id: "UPI ID",
    amount: "Amount",
    open_upi_app: "Open UPI App to Pay",
    enter_txn_ref: "Enter UPI Transaction ID after paying",
    submit_payment: "Submit Payment",
    appointment_slip: "Appointment Slip",
    payment_pending: "Payment pending",
    payment_done: "Payment done — verified by clinic",
    payment_self_reported: "Payment self-reported · awaiting verification",
    digital_slip: "Digital Appointment Slip",
    save_screenshot: "Tip: save a screenshot of this slip.",
    no_upi_configured: "This provider hasn't configured UPI yet. Please pay at counter.",
    language: "Language",
  },
  hi: {
    app_name: "उपचार डॉक्टर ऐप",
    find_doctor: "डॉक्टर खोजें",
    book_now: "अभी बुक करें",
    back: "वापस",
    loading: "लोड हो रहा है...",
    cancel: "रद्द करें",
    confirm: "पुष्टि करें",
    patient_app_title: "अपने नज़दीक भरोसेमंद डॉक्टर खोजें",
    patient_app_subtitle: "नाम, शहर या विशेषज्ञता से खोजें। तुरंत बुक करें। अपनी बारी लाइव देखें।",
    search_placeholder: "डॉक्टर का नाम, विशेषज्ञता या अस्पताल",
    city_placeholder: "शहर (वैकल्पिक)",
    specialization_label: "विशेषज्ञता",
    all_specializations: "सभी विशेषज्ञताएँ",
    use_my_location: "मेरा स्थान उपयोग करें",
    no_results: "कोई डॉक्टर नहीं मिला। कृपया दूसरी खोज करें।",
    consultation_fee: "परामर्श शुल्क",
    room_cabin: "कक्ष",
    avg_diag_time: "औसत जाँच समय",
    qualification: "योग्यता",
    working_hours: "कार्य समय",
    current_token: "वर्तमान टोकन",
    waiting: "प्रतीक्षा में",
    completed: "पूर्ण",
    your_position: "आपकी बारी",
    estimated_wait: "अनुमानित प्रतीक्षा",
    minutes: "मिनट",
    live: "लाइव",
    on_leave: "डॉक्टर अभी अवकाश पर हैं",
    book_appointment: "अपॉइंटमेंट बुक करें",
    your_name: "आपका नाम",
    your_age: "उम्र",
    your_mobile: "मोबाइल नंबर",
    pay_and_book: "UPI से भुगतान करें और बुक करें",
    booking_confirmed: "बुकिंग पुष्टि",
    token_number: "टोकन नंबर",
    pay_via_upi: "UPI से भुगतान",
    upi_id: "UPI आईडी",
    amount: "राशि",
    open_upi_app: "UPI ऐप खोलें",
    enter_txn_ref: "भुगतान के बाद UPI लेन-देन ID दर्ज करें",
    submit_payment: "भुगतान सबमिट करें",
    appointment_slip: "अपॉइंटमेंट पर्ची",
    payment_pending: "भुगतान बाकी",
    payment_done: "भुगतान सत्यापित",
    payment_self_reported: "भुगतान दर्ज · सत्यापन प्रतीक्षित",
    digital_slip: "डिजिटल अपॉइंटमेंट पर्ची",
    save_screenshot: "सुझाव: इस पर्ची का स्क्रीनशॉट सहेजें।",
    no_upi_configured: "इस प्रदाता ने UPI सेट नहीं किया है। कृपया काउंटर पर भुगतान करें।",
    language: "भाषा",
  },
};

const I18nContext = createContext({ lang: "en", t: (k) => k, setLang: () => {} });

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    if (typeof window === "undefined") return "en";
    return localStorage.getItem("upchaar_lang") || "en";
  });

  const setLang = useCallback((l) => {
    setLangState(l);
    if (typeof window !== "undefined") localStorage.setItem("upchaar_lang", l);
  }, []);

  const t = useCallback((key) => (DICT[lang] && DICT[lang][key]) || DICT.en[key] || key, [lang]);

  useEffect(() => {
    if (typeof document !== "undefined") document.documentElement.lang = lang;
  }, [lang]);

  return (
    <I18nContext.Provider value={{ lang, setLang, t }}>{children}</I18nContext.Provider>
  );
}

export const useI18n = () => useContext(I18nContext);
